import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui/layout/Stack';
import { Box, Popover, Text } from '@/ui/primitives';
import { TagsFilterField } from './TagsFilterField';
import type { ReactElement, ReactNode } from 'react';

const popoverFontSize = 13;

type DropdownFilterOption = {
  value: string;
  label: string;
};

type DropdownFiltersPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  heading?: string;
  headingIcon?: ReactNode;
  selectedStatuses: string[];
  onStatusesChange: (next: string[]) => void;
  statusOptions: DropdownFilterOption[];
  statusLabel?: string;
  selectedEntities?: string[];
  onEntitiesChange?: (next: string[]) => void;
  entityOptions?: DropdownFilterOption[];
  entityLabel?: string;
  selectedSuites?: string[];
  onSuitesChange?: (next: string[]) => void;
  suiteOptions?: DropdownFilterOption[];
  suiteLabel?: string;
  selectedTags: string[];
  onTagsChange: (next: string[]) => void;
  tagOptions: DropdownFilterOption[];
};

export const DropdownFiltersPopover = ({
  open,
  anchorEl,
  onClose,
  heading,
  headingIcon,
  selectedStatuses,
  onStatusesChange,
  statusOptions,
  statusLabel = 'Latest Status',
  selectedEntities,
  onEntitiesChange,
  entityOptions,
  entityLabel = 'Item',
  selectedSuites,
  onSuitesChange,
  suiteOptions,
  suiteLabel = 'Suite',
  selectedTags,
  onTagsChange,
  tagOptions,
}: DropdownFiltersPopoverProps): ReactElement => {
  const hasSuiteFilter =
    selectedSuites !== undefined && onSuitesChange !== undefined && suiteOptions !== undefined;
  const hasEntityFilter =
    selectedEntities !== undefined && onEntitiesChange !== undefined && entityOptions !== undefined;
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        '& .MuiPopover-paper': {
          borderRadius: `${r('md')} !important`,
          overflow: 'visible !important',
        },
      }}
      paperSx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${r('md')} !important`,
        overflow: 'visible !important',
        boxShadow: 'none',
        minWidth: 0,
        width: 'fit-content',
      }}
    >
      <Stack gap="none" sx={{ borderRadius: `${r('md')} !important`, overflow: 'hidden' }}>
        {heading ? (
          <Stack
            gap="none"
            sx={{
              px: s('lg'),
              py: s('md'),
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap="sm"
              sx={{ '& .MuiSvgIcon-root': { color: 'text.secondary' } }}
            >
              {headingIcon ?? null}
              <Text
                role="label"
                color="textSecondary"
                sx={{ fontSize: `${popoverFontSize}px`, lineHeight: 1.4 }}
              >
                {heading}
              </Text>
            </Stack>
          </Stack>
        ) : null}
        <Box
          sx={{
            pl: s('lg'),
            pr: s('lg'),
            py: s('lg'),
            width: 'fit-content',
            minWidth: 'fit-content',
          }}
        >
          <Stack gap="md" sx={{ justifyContent: 'flex-start', alignItems: 'flex-end' }}>
            <TagsFilterField
              label={statusLabel}
              fontSizePx={popoverFontSize}
              labelPlacement="inline"
              labelControlGap={s('md')}
              inputAriaLabel={`Filter by ${statusLabel.toLowerCase()}`}
              value={selectedStatuses}
              options={statusOptions}
              onChange={onStatusesChange}
              emptyLabel="All"
              controlWidthPx={180}
              inputBackgroundColor="background.default"
              inputLeftPadding={s('xs')}
            />
            {hasEntityFilter ? (
              <TagsFilterField
                label={entityLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${entityLabel.toLowerCase()}`}
                value={selectedEntities}
                options={entityOptions}
                emptyLabel="All"
                controlWidthPx={180}
                inputBackgroundColor="background.default"
                inputLeftPadding={s('xs')}
                onChange={onEntitiesChange}
              />
            ) : null}
            {hasSuiteFilter ? (
              <TagsFilterField
                label={suiteLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${suiteLabel.toLowerCase()}`}
                value={selectedSuites}
                options={suiteOptions}
                emptyLabel="All"
                controlWidthPx={180}
                inputBackgroundColor="background.default"
                inputLeftPadding={s('xs')}
                onChange={onSuitesChange}
              />
            ) : null}
            <TagsFilterField
              label="Tags"
              fontSizePx={popoverFontSize}
              labelPlacement="inline"
              labelControlGap={s('md')}
              inputAriaLabel="Filter by tags"
              value={selectedTags}
              options={tagOptions}
              onChange={onTagsChange}
              emptyLabel={tagOptions.length > 0 ? 'All' : 'No tags available'}
              controlWidthPx={180}
              inputBackgroundColor="background.default"
              inputLeftPadding={s('xs')}
            />
          </Stack>
        </Box>
      </Stack>
    </Popover>
  );
};

export type { DropdownFilterOption, DropdownFiltersPopoverProps };
